import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Eye, EyeOff, Trash2, Pencil, X } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useCounters } from "../../contexts/CountersContext";

const RANGE_LABELS = [
    { key: "all",      label: "Tutte" },
    { key: "upcoming", label: "In corso" },
    { key: "today",    label: "Oggi" },
    { key: "overdue",  label: "Scadute" },
];
const VIS_LABELS = [
    { key: "all",   label: "Tutte" },
    { key: "true",  label: "Visibili al cliente" },
    { key: "false", label: "Solo interne" },
];

function fmtItDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtItDateTime(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function statusFor(item) {
    if (item.is_overdue) return { key: "scaduta", label: "Scaduta" };
    if (item.days_to_due === 0) return { key: "oggi", label: "Oggi" };
    if (item.days_to_due > 0 && item.days_to_due <= 7) return { key: "soon", label: "In scadenza" };
    return { key: "scheduled", label: "Programmata" };
}

function StatusChip({ item }) {
    const s = statusFor(item);
    return <span className={`lt-chip-scad lt-chip-scad--${s.key}`} data-testid={`scad-status-${s.key}`}>
        {s.key !== "scheduled" && <span className="dot" />}{s.label}
    </span>;
}

function VisibilityBadge({ visible }) {
    if (visible) return (
        <span className="lt-visibility lt-visibility--public" data-testid="scad-vis-public">
            <Eye size={12} strokeWidth={2} /> Visibile al cliente
        </span>
    );
    return (
        <span className="lt-visibility lt-visibility--internal" data-testid="scad-vis-internal">
            <EyeOff size={12} strokeWidth={2} /> Interna
        </span>
    );
}

function ScadenzaForm({ initial, clients, onSave, onCancel, onDelete, saving }) {
    const isEdit = Boolean(initial?.id);
    const [clientId, setClientId] = useState(initial?.client?.id || "");
    const [title, setTitle] = useState(initial?.title || "");
    const [dueDate, setDueDate] = useState(initial?.due_date || "");
    const [visibleToClient, setVisibleToClient] = useState(Boolean(initial?.visible_to_client));
    const [notes, setNotes] = useState(initial?.notes || "");

    const submit = (e) => {
        e.preventDefault();
        if (!clientId || !title.trim() || !dueDate) {
            toast.error("Compila i campi obbligatori");
            return;
        }
        const payload = isEdit
            ? { title: title.trim(), due_date: dueDate, visible_to_client: visibleToClient, notes: notes || null }
            : { client_id: clientId, title: title.trim(), due_date: dueDate, visible_to_client: visibleToClient, notes: notes || null };
        onSave(payload);
    };

    return (
        <form onSubmit={submit} className="lt-scad-form" data-testid="scad-form">
            <div className="lt-form-row">
                <label className="lt-label">Cliente</label>
                <select
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    disabled={isEdit}
                    required
                    className="lt-select"
                    data-testid="scad-form-client"
                >
                    <option value="">Seleziona…</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                </select>
            </div>
            <div className="lt-form-row">
                <label className="lt-label">Titolo</label>
                <input className="lt-input" value={title} onChange={(e) => setTitle(e.target.value)} required minLength={3} maxLength={200} data-testid="scad-form-title" />
            </div>
            <div className="lt-form-row">
                <label className="lt-label">Data scadenza</label>
                <input type="date" className="lt-input" value={dueDate} onChange={(e) => setDueDate(e.target.value)} required data-testid="scad-form-due-date" />
            </div>
            <div className="lt-form-row">
                <label className="lt-label">Note</label>
                <textarea className="lt-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} maxLength={2000} data-testid="scad-form-notes" />
            </div>
            <div className="lt-toggle-row" data-testid="scad-form-visibility-row">
                <label className="lt-toggle">
                    <input type="checkbox" checked={visibleToClient} onChange={(e) => setVisibleToClient(e.target.checked)} data-testid="scad-form-visibility" />
                    <span className="lt-toggle__track"><span className="lt-toggle__thumb" /></span>
                    <span className="lt-toggle__label">Visibile al cliente</span>
                </label>
                <p className="lt-toggle__hint">Quando attivo, la scadenza appare nel portale del cliente.</p>
            </div>
            <div className="lt-form-actions">
                {isEdit && onDelete && (
                    <button type="button" className="lt-btn-danger-text" onClick={onDelete} data-testid="scad-form-delete">
                        <Trash2 size={14} /> Elimina
                    </button>
                )}
                <div style={{ flex: 1 }} />
                <button type="button" className="lt-btn-secondary" onClick={onCancel} data-testid="scad-form-cancel">Annulla</button>
                <button type="submit" className="lt-btn-primary" disabled={saving} data-testid="scad-form-save">Salva</button>
            </div>
        </form>
    );
}

function ConfirmDelete({ onConfirm, onCancel }) {
    return (
        <div className="lt-confirm" data-testid="scad-confirm">
            <h3 className="lt-confirm__title">Eliminare la scadenza?</h3>
            <p className="lt-confirm__sub">L'operazione non può essere annullata.</p>
            <div className="lt-form-actions">
                <div style={{ flex: 1 }} />
                <button className="lt-btn-secondary" onClick={onCancel} data-testid="scad-confirm-cancel">Annulla</button>
                <button className="lt-btn-danger" onClick={onConfirm} data-testid="scad-confirm-yes">Elimina</button>
            </div>
        </div>
    );
}

export default function Scadenze() {
    const { counters, refresh: refreshCounters } = useCounters();
    const sc = counters?.scadenze || {};
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [q, setQ] = useState("");
    const [clientFilter, setClientFilter] = useState("");
    const [rangeFilter, setRangeFilter] = useState("all");
    const [visFilter, setVisFilter] = useState("all");
    const [clients, setClients] = useState([]);
    const [modal, setModal] = useState(null); // {mode: 'create'|'edit', item?: ...}
    const [confirm, setConfirm] = useState(null); // {id, title}
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const params = { range: rangeFilter, visible_to_client: visFilter, page: 1, page_size: 100 };
            if (q.trim()) params.q = q.trim();
            if (clientFilter) params.client_id = clientFilter;
            const { data } = await api.get("/internal/scadenze", { params });
            setItems(data.items);
            setTotal(data.total);
        } catch {
            toast.error("Errore caricamento scadenze");
        } finally {
            setLoading(false);
        }
    }, [q, clientFilter, rangeFilter, visFilter]);

    useEffect(() => {
        api.get("/internal/clients", { params: { status: "active", page_size: 200 } })
            .then(({ data }) => setClients(data.items || data))
            .catch(() => {});
    }, []);
    useEffect(() => { fetchList(); }, [fetchList]);

    const save = async (payload) => {
        setSaving(true);
        try {
            if (modal.mode === "create") {
                await api.post("/internal/scadenze", payload);
                toast.success("Scadenza creata");
            } else {
                await api.patch(`/internal/scadenze/${modal.item.id}`, payload);
                toast.success("Scadenza aggiornata");
            }
            setModal(null);
            fetchList();
            refreshCounters();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore");
        } finally {
            setSaving(false);
        }
    };

    const doDelete = async (id) => {
        try {
            await api.delete(`/internal/scadenze/${id}`);
            toast.success("Scadenza eliminata");
            setConfirm(null);
            setModal(null);
            fetchList();
            refreshCounters();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore");
        }
    };

    const counterCards = useMemo(() => [
        { key: "in_corso",        label: "In corso",              value: sc.in_corso ?? 0 },
        { key: "in_scadenza_7gg", label: "In scadenza (7 giorni)", value: sc.in_scadenza_7gg ?? 0 },
        { key: "scadute",         label: "Scadute",                value: sc.scadute ?? 0, danger: true },
        { key: "visibili",        label: "Visibili al cliente",   value: sc.visibili_al_cliente ?? 0 },
    ], [sc]);

    return (
        <div style={{ padding: "32px 40px" }} data-testid="scad-page">
            <div className="flex items-center" style={{ marginBottom: 20 }}>
                <h1 className="lt-h1" style={{ flex: 1 }}>Scadenze</h1>
                <button className="lt-btn-primary" onClick={() => setModal({ mode: "create" })} data-testid="scad-create">
                    <Plus size={14} /> Nuova scadenza
                </button>
            </div>

            <div className="grid" style={{ gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 20 }} data-testid="scad-counters">
                {counterCards.map((c) => (
                    <div key={c.key} className="lt-counter-card" style={{ cursor: "default" }} data-testid={`scad-counter-${c.key}`}>
                        <div className="lt-counter-card__value">
                            {c.value}
                            {c.danger && c.value > 0 && <span className="lt-chip-overdue-mini" style={{ marginLeft: 8 }}>!</span>}
                        </div>
                        <div className="lt-counter-card__label">{c.label}</div>
                    </div>
                ))}
            </div>

            <div className="flex items-center" style={{ gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
                <input
                    type="search"
                    className="lt-input"
                    placeholder="Cerca per titolo…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    style={{ width: 240 }}
                    data-testid="scad-search"
                />
                <select className="lt-select" value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} data-testid="scad-filter-client">
                    <option value="">Tutti i clienti</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                </select>
                <div className="lt-segmented" data-testid="scad-filter-range">
                    {RANGE_LABELS.map((r) => (
                        <button key={r.key} type="button" className={"lt-segmented__btn" + (rangeFilter === r.key ? " is-active" : "")} onClick={() => setRangeFilter(r.key)}>{r.label}</button>
                    ))}
                </div>
                <select className="lt-select" value={visFilter} onChange={(e) => setVisFilter(e.target.value)} data-testid="scad-filter-vis">
                    {VIS_LABELS.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
                </select>
            </div>

            <div className="lt-card" style={{ padding: 0 }} data-testid="scad-table-wrap">
                <table className="lt-table" data-testid="scad-table">
                    <thead>
                        <tr>
                            <th style={{ width: 130 }}>Stato</th>
                            <th>Titolo</th>
                            <th>Cliente</th>
                            <th style={{ width: 140 }}>Data scadenza</th>
                            <th style={{ width: 180 }}>Visibilità</th>
                            <th style={{ width: 170 }} className="hidden lg:table-cell">Aggiornata il</th>
                            <th style={{ width: 56 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={7} style={{ padding: 24, color: "#8A8F88" }}>Caricamento…</td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan={7} style={{ padding: 24, color: "#8A8F88" }}>Nessuna scadenza con i filtri correnti.</td></tr>
                        ) : (
                            items.map((it) => (
                                <tr key={it.id} data-testid={`scad-row-${it.id}`} onClick={() => setModal({ mode: "edit", item: it })} style={{ cursor: "pointer" }}>
                                    <td><StatusChip item={it} /></td>
                                    <td><span style={{ fontWeight: 500, color: "#181B20" }}>{it.title}</span></td>
                                    <td><span style={{ color: "#5F6560" }}>{it.client?.company_name || "—"}</span></td>
                                    <td>{fmtItDate(it.due_date)}</td>
                                    <td><VisibilityBadge visible={it.visible_to_client} /></td>
                                    <td className="hidden lg:table-cell" style={{ color: "#5F6560", fontSize: 13 }} data-testid={`scad-updated-${it.id}`}>{fmtItDateTime(it.updated_at)}</td>
                                    <td onClick={(e) => e.stopPropagation()}>
                                        <button className="lt-icon-btn" onClick={() => setModal({ mode: "edit", item: it })} title="Modifica" data-testid={`scad-edit-${it.id}`}>
                                            <Pencil size={14} />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {modal && !confirm && (
                <div className="lt-modal-backdrop" onClick={() => setModal(null)}>
                    <div className="lt-modal" onClick={(e) => e.stopPropagation()} data-testid="scad-modal">
                        <div className="lt-modal__head">
                            <h2 className="lt-modal__title">{modal.mode === "create" ? "Nuova scadenza" : "Modifica scadenza"}</h2>
                            <button className="lt-icon-btn" onClick={() => setModal(null)} aria-label="Chiudi" data-testid="scad-modal-close"><X size={16} /></button>
                        </div>
                        <ScadenzaForm
                            initial={modal.item}
                            clients={clients}
                            onSave={save}
                            onCancel={() => setModal(null)}
                            onDelete={modal.mode === "edit" ? () => setConfirm({ id: modal.item.id, title: modal.item.title }) : undefined}
                            saving={saving}
                        />
                    </div>
                </div>
            )}

            {confirm && (
                <div className="lt-modal-backdrop" onClick={() => setConfirm(null)}>
                    <div className="lt-modal lt-modal--small" onClick={(e) => e.stopPropagation()}>
                        <ConfirmDelete
                            onConfirm={() => doDelete(confirm.id)}
                            onCancel={() => setConfirm(null)}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
